import { IWorkflow, IWorkflowInput } from "@/interfaces/workflow.interface";

type WorkflowLegacy = Record<string, unknown>;

const readNullableString = (
  source: WorkflowLegacy,
  camelKey: string,
  pascalKey: string
): string | null => {
  const value = source[camelKey] ?? source[pascalKey];
  if (value === undefined || value === null || value === "") {
    return null;
  }

  return String(value);
};

const readNullableNumber = (
  source: WorkflowLegacy,
  camelKey: string,
  pascalKey: string
): number | null => {
  const value = source[camelKey] ?? source[pascalKey];
  if (value === undefined || value === null || value === "") {
    return null;
  }

  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

export const readWorkflowKeyWords = (workflow?: IWorkflowInput | IWorkflow | null): string | null => {
  if (!workflow) {
    return null;
  }

  const source = workflow as WorkflowLegacy;
  const raw = source.workflowKeyWords ?? source.WorkflowKeyWords;
  return typeof raw === "string" ? raw : null;
};

export const buildWorkflowUpdatePayload = (
  workflow: IWorkflowInput | IWorkflow,
  overrides: Partial<IWorkflowInput> = {}
): IWorkflowInput => {
  const source = workflow as WorkflowLegacy;
  const productDisplayTypeRaw = source.productDisplayType ?? source.ProductDisplayType ?? "LIST";
  const normalizedDisplayType =
    String(productDisplayTypeRaw).toUpperCase() === "CAROUSEL" ? "CAROUSEL" : "LIST";

  const orderReportLayoutRaw = source.orderReportLayout ?? source.OrderReportLayout;
  const orderReportLayout =
    String(orderReportLayoutRaw ?? "").toUpperCase() === "R" ? "R" : "A";

  const assertivenessRaw = source.productSearchAssertiveness ?? source.ProductSearchAssertiveness;
  const assertivenessNum =
    assertivenessRaw === null || assertivenessRaw === undefined ? 50 : Number(assertivenessRaw);

  const conversationTimeoutRaw = source.conversationTimeout ?? source.ConversationTimeout;
  const conversationTimeout =
    typeof conversationTimeoutRaw === "number" && Number.isFinite(conversationTimeoutRaw)
      ? conversationTimeoutRaw
      : 180;

  return {
    name: workflow.name || "",
    displayName: workflow.displayName || "",
    description: workflow.description || "",
    active: workflow.active ?? false,
    workflowTypeId: workflow.workflowTypeId || 1,
    productDisplayType: normalizedDisplayType,
    orderReportLayout,
    productSearchAssertiveness: Number.isFinite(assertivenessNum)
      ? Math.min(100, Math.max(0, assertivenessNum))
      : 50,
    conversationTimeout,
    expirationStepName: readNullableString(source, "expirationStepName", "ExpirationStepName"),
    reactivationTimeout: readNullableNumber(source, "reactivationTimeout", "ReactivationTimeout"),
    reactivationMessage: readNullableString(source, "reactivationMessage", "ReactivationMessage"),
    reactivationMaxMessages: readNullableNumber(
      source,
      "reactivationMaxMessages",
      "ReactivationMaxMessages"
    ),
    reactivationInterval: readNullableNumber(source, "reactivationInterval", "ReactivationInterval"),
    workflowKeyWords: readWorkflowKeyWords(workflow),
    ...overrides,
  };
};
